import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Paper,
  Alert,
  Chip,
} from '@mui/material';
import { Product, productService, StockAdjustmentData } from '../../services/productService';
import { useAppDispatch } from '../../hooks/redux';
import { addNotification } from '../../store/slices/uiSlice';

interface StockAdjustmentDialogProps {
  open: boolean;
  onClose: () => void;
  product: Product | null;
  onSuccess: () => void;
}

const ADJUSTMENT_TYPES = [
  { value: 'PURCHASE', label: 'Purchase', description: 'Add stock from supplier' },
  { value: 'USAGE', label: 'Usage', description: 'Remove stock used in services' },
  { value: 'ADJUSTMENT', label: 'Adjustment', description: 'Manual stock correction' },
  { value: 'RETURN', label: 'Return', description: 'Return to supplier' },
];

const StockAdjustmentDialog: React.FC<StockAdjustmentDialogProps> = ({
  open,
  onClose,
  product,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<StockAdjustmentData>({
    quantity: 0,
    type: 'ADJUSTMENT',
    reason: '',
    unitCost: 0,
    referenceId: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  React.useEffect(() => {
    if (open && product) {
      setFormData({
        quantity: 0,
        type: 'ADJUSTMENT',
        reason: '',
        unitCost: product.costPrice,
        referenceId: '',
      });
      setErrors({});
    }
  }, [open, product]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.quantity === 0) {
      newErrors.quantity = 'Quantity cannot be zero';
    }

    if (!formData.reason.trim()) {
      newErrors.reason = 'Reason is required';
    }

    if ((formData.type === 'PURCHASE' || formData.type === 'ADJUSTMENT') && (formData.unitCost || 0) <= 0) {
      newErrors.unitCost = 'Unit cost must be greater than 0';
    }

    // Check if removing more stock than available
    if ((formData.type === 'USAGE' || formData.type === 'RETURN') &&
      Math.abs(formData.quantity) > (product?.currentStock || 0)) {
      newErrors.quantity = `Cannot remove more than current stock (${product?.currentStock})`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!product || !validateForm()) return;

    setLoading(true);
    try {
      await productService.adjustStock(product.id, formData);

      dispatch(addNotification({
        type: 'success',
        message: 'Stock adjusted successfully',
      }));

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error adjusting stock:', error);
      dispatch(addNotification({
        type: 'error',
        message: error.response?.data?.error || 'Failed to adjust stock',
      }));
    } finally {
      setLoading(false);
    }
  };

  const calculateNewStock = (): number => {
    if (!product) return 0;

    let newStock = product.currentStock;
    if (formData.type === 'PURCHASE' || formData.type === 'ADJUSTMENT') {
      newStock += Math.abs(formData.quantity);
    } else if (formData.type === 'USAGE' || formData.type === 'RETURN') {
      newStock -= Math.abs(formData.quantity);
    }

    return Math.max(0, newStock);
  };

  const getStockChangeColor = (): string => {
    const newStock = calculateNewStock();
    if (!product) return 'text.primary';

    if (newStock < product.currentStock) return '#EF4444';
    if (newStock > product.currentStock) return '#10B981';
    return 'text.primary';
  };

  if (!product) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px)',
        },
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #E8B4B8 0%, #F5D5D8 100%)',
          color: 'white',
          fontWeight: 800,
          fontSize: '1.3rem',
          textAlign: 'center',
          py: 3,
        }}
      >
        Stock Adjustment
      </DialogTitle>

      <DialogContent sx={{ p: 4 }}>
        {/* Product Info */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            bgcolor: 'rgba(232, 180, 184, 0.05)',
            borderRadius: 3,
            border: '1px solid rgba(232, 180, 184, 0.1)',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#E8B4B8', mb: 2 }}>
            Product Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                {product.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {product.brand} • {product.sku}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Current Stock
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {product.currentStock} {product.unit}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                New Stock
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: getStockChangeColor(),
                }}
              >
                {calculateNewStock()} {product.unit}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Adjustment Form */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors.type}>
              <InputLabel>Adjustment Type</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as any }))}
                label="Adjustment Type"
                sx={{
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E8B4B8' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#E8B4B8' },
                }}
              >
                {ADJUSTMENT_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {type.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {type.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Quantity"
              type="number"
              value={formData.quantity}
              onChange={(e) => setFormData(prev => ({ ...prev, quantity: Number(e.target.value) }))}
              error={!!errors.quantity}
              helperText={errors.quantity}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': { borderColor: '#E8B4B8' },
                  '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Unit Cost (₹)"
              type="number"
              value={formData.unitCost || 0}
              onChange={(e) => setFormData(prev => ({ ...prev, unitCost: Number(e.target.value) }))}
              error={!!errors.unitCost}
              helperText={errors.unitCost}
              disabled={formData.type === 'USAGE' || formData.type === 'RETURN'}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': { borderColor: '#E8B4B8' },
                  '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                },
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Reason"
              multiline
              rows={3}
              value={formData.reason}
              onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
              error={!!errors.reason}
              helperText={errors.reason}
              placeholder="Enter reason for stock adjustment..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': { borderColor: '#E8B4B8' },
                  '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                },
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Reference ID (Optional)"
              value={formData.referenceId}
              onChange={(e) => setFormData(prev => ({ ...prev, referenceId: e.target.value }))}
              placeholder="Appointment ID, Purchase Order, etc."
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': { borderColor: '#E8B4B8' },
                  '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                },
              }}
            />
          </Grid>
        </Grid>

        {/* Stock Level Warning */}
        {calculateNewStock() <= product.minStockLevel && (
          <Alert
            severity="warning"
            sx={{
              mt: 3,
              borderRadius: 2,
              bgcolor: 'rgba(237, 137, 54, 0.1)',
              border: '1px solid rgba(237, 137, 54, 0.2)',
            }}
          >
            <Typography variant="body2">
              <strong>Low Stock Warning:</strong> The new stock level ({calculateNewStock()} {product.unit})
              will be at or below the minimum stock level ({product.minStockLevel} {product.unit}).
            </Typography>
          </Alert>
        )}

        {/* Summary */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mt: 3,
            bgcolor: 'rgba(6, 182, 212, 0.05)',
            borderRadius: 3,
            border: '1px solid rgba(6, 182, 212, 0.1)',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#06B6D4', mb: 2 }}>
            Adjustment Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Adjustment Type
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  label={ADJUSTMENT_TYPES.find(t => t.value === formData.type)?.label}
                  size="small"
                  sx={{
                    bgcolor: formData.type === 'PURCHASE' || formData.type === 'ADJUSTMENT'
                      ? 'rgba(16, 185, 129, 0.1)'
                      : 'rgba(239, 68, 68, 0.1)',
                    color: formData.type === 'PURCHASE' || formData.type === 'ADJUSTMENT'
                      ? '#10B981'
                      : '#EF4444',
                    fontWeight: 600,
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Stock Change
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 700,
                  color: getStockChangeColor(),
                  mt: 0.5,
                }}
              >
                {formData.type === 'PURCHASE' || formData.type === 'ADJUSTMENT' ? '+' : '-'}
                {Math.abs(formData.quantity)} {product.unit}
              </Typography>
            </Grid>
            {((formData.unitCost || 0) > 0) && (formData.type === 'PURCHASE' || formData.type === 'ADJUSTMENT') && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Total Cost
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, mt: 0.5 }}>
                  ₹{((formData.unitCost || 0) * Math.abs(formData.quantity)).toFixed(2)}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button
          onClick={onClose}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 3,
            fontWeight: 600,
            color: '#666',
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 3,
            fontWeight: 700,
            bgcolor: '#E8B4B8',
            '&:hover': {
              bgcolor: '#D4A4A8',
            },
          }}
        >
          {loading ? 'Adjusting...' : 'Adjust Stock'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StockAdjustmentDialog;